import { ITargetingDefinitionV2 } from 'features/targetingV2/types/targeting';
import { OptionType } from 'interfaces';
import {
  AuctionType,
  DealLegacyStatus,
  DspIncludedSeats,
  SalesChannelLegacyStatus,
  TargetingVersion,
  Territory,
} from 'interfaces/generated.types';
import memoizeOne from 'memoize-one';
import { CurrenciesEnum } from 'utils/currency';
import { TimeZones } from 'utils/date';
import { generatePriorityTypes } from 'utils/generators';
import {
  ITargetingGroup,
  ITargetingMasterTemplate,
} from 'utils/targetingDefinitions';

export const priorityTypes = [...generatePriorityTypes()];

export interface IDealFormValues {
  auctionType: AuctionType;
  offsetCpm: string;
  currency: CurrenciesEnum;
  endDate: Date | null;
  externalId: string;
  floorCpm: string;
  id: string;
  altId?: number;
  name: string;
  priority: number;
  dsp: OptionType | null;
  seats: OptionType[] | [];
  startDate: Date | null;
  overridePublisherFloor: boolean;
  privateAuction: boolean;
  salesChannelName: string;
  salesChannelId: string;
  timeZone: TimeZones;
  trusted: boolean;
  journey: string;
  owner: OptionType | null;
  status?: DealLegacyStatus;
  targetingGroups: ITargetingGroup[];
  targetingMasterTemplates: ITargetingMasterTemplate[];
  targetingGeneralGroup: ITargetingGroup | null;
  adSeparation: string;
  hasAdSeparation: boolean;
  noEndDate: boolean;
  adExclusive: boolean;
  territory: Territory;
  allSeats?: boolean;
  allKnownSeats?: boolean;
  dspIncludedSeats: DspIncludedSeats;
  targetingVersion: TargetingVersion;
  targetingDefinitionV2: ITargetingDefinitionV2 | null;
}

export const actionTypes = [
  {
    value: AuctionType.FirstPrice,
    label: '1st price',
  },
  {
    value: AuctionType.SecondPrice,
    label: '2nd price',
  },
  {
    value: AuctionType.FixedPrice,
    label: 'Fixed price',
  },
];

export const dealStatusValues = [
  {
    value: DealLegacyStatus.Draft,
    label: 'Draft',
    isDisabled: (value: DealLegacyStatus) =>
      [DealLegacyStatus.Ready, DealLegacyStatus.Active, DealLegacyStatus.Inactive].includes(
        value
      ),
  },
  {
    value: DealLegacyStatus.Ready,
    label: 'Ready',
    isDisabled: (value: DealLegacyStatus) =>
      [DealLegacyStatus.Draft, DealLegacyStatus.Active, DealLegacyStatus.Inactive].includes(
        value
      ),
  },
  {
    value: DealLegacyStatus.Active,
    label: 'Active',
    isDisabled: (value: DealLegacyStatus) =>
      [DealLegacyStatus.Draft, DealLegacyStatus.Ready].includes(value),
  },
  {
    value: DealLegacyStatus.Inactive,
    label: 'Inactive',
    isDisabled: (value: DealLegacyStatus) =>
      [DealLegacyStatus.Draft, DealLegacyStatus.Ready].includes(value),
  },
];

export const getDealStatusData = memoizeOne(
  (values: IDealFormValues, salesChannelStatus: SalesChannelLegacyStatus) => [
    {
      status: DealLegacyStatus.Draft,
      required: [
        ...(!values.name ? ["Complete 'Name'"] : []),
        ...(!values.salesChannelName ? ['Complete Parent Sales Channel'] : []),
      ],
    },
    {
      status: DealLegacyStatus.Ready,
      required: [
        ...(!values.name ? ['Waiting for Name'] : []),
        ...(salesChannelStatus !== SalesChannelLegacyStatus.Active
          ? ['Waiting for Parent Sales Channel to be in ACTIVE state']
          : []),
        ...(!values.externalId ? ['Waiting for External ID'] : []),
        ...(!values.startDate ? ['Waiting for Start Date'] : []),
        ...(values.auctionType === AuctionType.AuctionTypeUnspecified
          ? ['Waiting for Auction Type']
          : []),
        ...(!values.floorCpm ? ['Waiting for Floor CPM'] : []),
        ...(!values.priority ? ['Waiting for Priority'] : []),
      ],
    },
    {
      status:
        values.status === DealLegacyStatus.Inactive
          ? DealLegacyStatus.Inactive
          : DealLegacyStatus.Active,
      required: ['Waiting for Start Date'],
    },
  ]
);

export const dealDetailsFields = [
  'name',
  'owner',
  'priority',
  'auctionType',
  'currency',
  'floorCpm',
  'overridePublisherFloor',
  'offsetCpm',
  'timeZone',
  'startDate',
  'endDate',
  'adSeparation',
  'territory',
];

export const rtbConnectionFields = ['dsp', 'seats', 'externalId'];
